/**
 * ArrangementPhotoThumbnail — read-only, tappable thumbnail of an order's
 * designer arrangement photo, with a full-screen lightbox. Renders nothing
 * when the order has no photo.
 *
 * Used where authenticated staff/drivers need a quick visual of what was made:
 * the order's Fulfillment & Proofs tab and the driver route list.
 *
 * The on-screen thumbnail loads the small server-generated derivative
 * (`thumbnail_path`); the full-resolution original is fetched lazily, only when
 * the lightbox is opened. Older photos with no thumbnail fall back to the
 * original for both. Images are fetched as auth blobs via
 * GET /api/arrangement-photo/{path}. The public POD receipt does NOT use this —
 * it has no auth and serves the image through its own public endpoint.
 */
import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { API } from '@/config/constants';
import { useAuthenticatedMedia } from '@/hooks/useAuthenticatedMedia';
import { offlineMediaLabel } from '@/utils/mediaPresentation';

export function ArrangementPhotoThumbnail({ photo, className = '', testId = 'arrangement-thumb' }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const thumbPath = photo?.thumbnail_path || photo?.storage_path;
  const fullPath = photo?.storage_path;
  // When a separate thumbnail exists, the lightbox needs to pull the original;
  // otherwise the "thumb" already IS the original and no extra fetch is needed.
  const hasSeparateFull = !!photo?.thumbnail_path;

  const requests = useMemo(() => {
    const next = thumbPath ? [{
      key: 'thumb',
      url: `${API}/arrangement-photo/${encodeURIComponent(thumbPath)}`,
    }] : [];
    if (lightboxOpen && hasSeparateFull && fullPath) next.push({
      key: 'full',
      url: `${API}/arrangement-photo/${encodeURIComponent(fullPath)}`,
    });
    return next;
  }, [thumbPath, lightboxOpen, hasSeparateFull, fullPath]);
  const loaded = useAuthenticatedMedia(requests);
  const thumbUrl = loaded.thumb?.url || null;
  const fullUrl = loaded.full?.url || null;

  if (!thumbPath) return null;

  const closeLightbox = () => setLightboxOpen(false);
  const lightboxSrc = fullUrl || thumbUrl;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (thumbUrl) setLightboxOpen(true); }}
        className={`relative overflow-hidden rounded-lg bg-stone-100 border border-stone-200 flex-shrink-0 ${className}`}
        data-testid={testId}
        aria-label="View arrangement photo"
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt="Arrangement" className="w-full h-full object-cover" />
        ) : loaded.thumb?.state === 'loading' ? (
          <div className="w-full h-full animate-pulse bg-stone-200" />
        ) : (
          <div className="w-full h-full flex items-center justify-center p-1 text-center text-[10px] text-stone-500">
            {offlineMediaLabel(loaded.thumb)}
          </div>
        )}
      </button>

      {lightboxOpen && lightboxSrc && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid={`${testId}-lightbox`}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxSrc}
            alt="Arrangement full size"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

export default ArrangementPhotoThumbnail;
